import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import {
  Activity,
  BookOpen,
  BadgeDollarSign,
  Building2,
  FolderKanban,
  Home,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldUser,
  Wallet,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useUser } from "@/queries/user";
import { useUserProjects } from "@/queries/orgs";
import { supabase } from "@/utils/supabase";
import { ApiError } from "@/utils/fetch";
import { cn } from "@/lib/utils";
import { BlurredEmail } from "@/components/blurred-email";
import { CookieNotice } from "@/components/cookie-notice";
import { PublicFooter } from "@/components/public-footer";

const PUBLIC_PATH_PREFIXES = [
  "/sign-in",
  "/log-in",
  "/sign-up",
  "/docs",
  "/blog",
  "/pricing",
  "/contact",
  "/faq",
  "/privacy",
  "/terms",
  "/cookies",
  "/invitations/accept",
];

function isPublicPath(path: string) {
  return (
    path === "/" ||
    PUBLIC_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
  );
}

export function RootComponent() {
  const navigate = useNavigate();
  const router = useRouterState();
  const qc = useQueryClient();
  const path = router.location.pathname;
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "SIGNED_OUT") {
        qc.cancelQueries();
        qc.clear();
      }
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  useEffect(() => {
    if (session === null && !isPublicPath(path)) {
      navigate({
        to: "/log-in",
        search: { next: path } as never,
        replace: true,
      });
    }
  }, [session, path, navigate]);

  // Public pages render outside the authenticated Shell regardless of session.
  if (isPublicPath(path)) return <><Outlet /><PublicFooter /><CookieNotice /></>;
  if (session === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  if (!session) return null;

  return <><Shell /><CookieNotice /></>;
}

function Shell() {
  const navigate = useNavigate();
  const user = useUser();
  const userProjects = useUserProjects(user.data?.user_id ?? "");
  const router = useRouterState();
  const path = router.location.pathname;
  const obscureEmail = user.data?.obscure_email ?? false;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [path]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/log-in" });
  }

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="relative min-h-screen xl:grid xl:grid-cols-[16rem_1fr]">
      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-sidebar-border bg-sidebar px-4 xl:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="rounded-md p-1 text-sidebar-foreground/70 hover:text-sidebar-foreground"
          aria-label="Open menu"
        >
          <Menu className="size-5" />
        </button>
        <div className="flex items-center gap-2 text-primary">
          <img
            src="/todollm-icon.png"
            alt="TodoLLM"
            className="h-6 w-6 rounded-lg"
          />
          <span className="text-sm font-semibold tracking-tight">TodoLLM</span>
        </div>
      </header>

      {/* Backdrop (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 xl:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-full w-64 flex-col gap-6 border-r border-sidebar-border bg-sidebar p-4 text-sidebar-foreground transition-transform duration-200 ease-in-out",
          "xl:sticky xl:top-0 xl:z-auto xl:h-screen xl:w-auto xl:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {/* Close button (mobile only) */}
        <button
          onClick={closeSidebar}
          className="absolute right-3 top-3 rounded-md p-1 text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground xl:hidden"
          aria-label="Close menu"
        >
          <X className="size-5" />
        </button>

        <div>
          <div className="flex items-center gap-2 text-primary">
            <img
              src="/todollm-icon.png"
              alt="TodoLLM"
              className="h-7 w-7 rounded-lg"
            />
            <span className="font-semibold tracking-tight">TodoLLM</span>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Avatar
              name={user.data?.display_name ?? user.data?.email ?? "User"}
              avatarUrl={user.data?.avatar_url ?? null}
            />
            <div className="min-w-0">
              {user.data?.display_name && (
                <p className="truncate text-sm font-medium">
                  {user.data.display_name}
                </p>
              )}
              {user.data?.email && (
                <BlurredEmail
                  email={user.data.email}
                  active={obscureEmail}
                  className="truncate text-xs text-muted-foreground"
                />
              )}
            </div>
          </div>
        </div>

        <Separator />

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto text-sm">
          <NavLink
            to="/"
            exact
            path={path}
            icon={<Home className="size-4" />}
            onNavigate={closeSidebar}
          >
            Home
          </NavLink>
          <NavLink
            to="/dashboard"
            path={path}
            icon={<LayoutDashboard className="size-4" />}
            onNavigate={closeSidebar}
          >
            Dashboard
          </NavLink>
          <p className="mt-4 mb-1 px-2 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
            Projects
          </p>
          {userProjects.data?.map((p) => (
            <NavLink
              key={p.id}
              to="/projects/$projectId"
              params={{ projectId: p.id }}
              path={path}
              icon={<FolderKanban className="size-4" />}
              onNavigate={closeSidebar}
            >
              {p.name}
            </NavLink>
          ))}
          {userProjects.data && userProjects.data.length === 0 && (
            <p className="px-2 text-xs text-muted-foreground">No projects yet</p>
          )}

          <p className="mt-4 mb-1 px-2 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
            Organizations
          </p>
          {user.data?.organizations.map((org) => (
            <NavLink
              key={org.org_id}
              to="/orgs/$orgId"
              params={{ orgId: org.org_id }}
              path={path}
              icon={<Building2 className="size-4" />}
              onNavigate={closeSidebar}
            >
              {org.name}
            </NavLink>
          ))}
          {user.data && user.data.organizations.length === 0 && (
            <p className="px-2 text-xs text-muted-foreground">No orgs yet</p>
          )}

          <p className="mt-4 mb-1 px-2 text-[0.65rem] uppercase tracking-wider text-muted-foreground">
            Settings
          </p>
          <NavLink
            to="/settings"
            path={path}
            icon={<ShieldUser className="size-4" />}
            onNavigate={closeSidebar}
          >
            Account
          </NavLink>
          <NavLink
            to="/billing"
            path={path}
            icon={<Wallet className="size-4" />}
            onNavigate={closeSidebar}
          >
            Billing
          </NavLink>
          <NavLink
            to="/settings/api-keys"
            path={path}
            icon={<KeyRound className="size-4" />}
            onNavigate={closeSidebar}
          >
            API keys
          </NavLink>
          <NavLink
            to="/pricing"
            path={path}
            icon={<BadgeDollarSign className="size-4" />}
            onNavigate={closeSidebar}
          >
            Pricing
          </NavLink>
          <NavLink
            to="/docs"
            path={path}
            icon={<BookOpen className="size-4" />}
            onNavigate={closeSidebar}
          >
            Docs
          </NavLink>
          <a
            href="https://stats.uptimerobot.com/m0exbqGCmL"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeSidebar}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Activity className="size-4" />
            Status
          </a>
        </nav>

        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 justify-start"
          onClick={signOut}
        >
          <LogOut className="size-4" />
          Sign out
        </Button>
      </aside>

      <main className="px-4 py-6 md:px-8 md:py-8">
        <div className="mx-auto max-w-5xl space-y-4">
          {user.error && (
            <AuthErrorBanner error={user.error as Error} onSignOut={signOut} />
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
}

interface NavLinkProps {
  to: string;
  path: string;
  exact?: boolean;
  icon?: React.ReactNode;
  params?: Record<string, string>;
  children: React.ReactNode;
  onNavigate?: () => void;
}

function NavLink({ to, path, exact, icon, params, children, onNavigate }: NavLinkProps) {
  const expanded = params
    ? to.replace(/\$(\w+)/g, (_, k) => params[k] ?? "")
    : to;
  const active = exact
    ? path === expanded
    : path === expanded || path.startsWith(`${expanded}/`);
  return (
    <Link
      to={to}
      params={params as never}
      onClick={onNavigate}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
      )}
    >
      {icon}
      <span className="truncate">{children}</span>
    </Link>
  );
}

function Avatar({
  name,
  avatarUrl,
}: {
  name: string;
  avatarUrl: string | null;
}) {
  const fallback = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2);

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="h-9 w-9 rounded-full border border-sidebar-border object-cover"
      />
    );
  }

  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full border border-sidebar-border bg-sidebar-accent text-xs font-semibold text-sidebar-accent-foreground">
      {fallback || "U"}
    </div>
  );
}

function AuthErrorBanner({
  error,
  onSignOut,
}: {
  error: Error;
  onSignOut: () => void;
}) {
  const paymentRequired = error instanceof ApiError && error.status === 402;
  if (paymentRequired) {
    return (
      <div
        role="alert"
        className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
      >
        <p className="font-medium">Settle your outstanding invoice</p>
        <p className="text-destructive/80">
          Your Pro account is past the 60-day grace window with an unpaid
          balance. Sign in to the customer portal sent to your email to
          settle the invoice. Your account will be restored automatically once
          payment clears.
        </p>
        <div className="pt-1">
          <Button variant="outline" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <p className="font-medium">Couldn't reach the API.</p>
        <p className="text-destructive/80">{error.message}</p>
      </div>
      <Button variant="outline" size="sm" onClick={onSignOut}>
        Sign out
      </Button>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="px-8 py-12">
      <h2 className="mb-2 text-2xl font-semibold">Not found</h2>
      <p className="mb-4 text-muted-foreground">
        The page you're looking for doesn't exist.
      </p>
      <Button asChild variant="outline">
        <Link to="/">Back home</Link>
      </Button>
    </div>
  );
}

export function ErrorPage({ error }: { error: Error }) {
  return (
    <div className="px-8 py-12">
      <h2 className="mb-2 text-2xl font-semibold text-destructive">Error</h2>
      <pre className="whitespace-pre-wrap rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error.message}
      </pre>
    </div>
  );
}
